import { useQuery } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import type { ExampleCompaniesCheck } from '@/modules/jobs/signal/exampleCompanies';
import type { EmployerSizeBand } from '@/modules/jobs/signal/types';

export interface ExampleCompaniesCheckInputs {
  /** The example companies as committed (on blur); empty checks nothing. */
  companies: string;
  employerSize: EmployerSizeBand;
  employerIndustries: string[];
  /** The job's workspace, where the check is logged; the caller's default when empty. */
  companyId: string;
}

/**
 * The job's example companies, found in Coresignal and checked against its size band and industries
 * (`/ai/example-companies`): a model call, so it runs on committed text only, and the same inputs are never checked
 * twice. A failure isn't retried and shows no note.
 */
export const useExampleCompaniesCheckQuery = ({
  companies,
  employerSize,
  employerIndustries,
  companyId,
}: ExampleCompaniesCheckInputs) => {
  const industries = [...employerIndustries].sort();
  return useQuery({
    queryKey: [
      'ai',
      'example-companies',
      companies,
      employerSize,
      industries,
      companyId,
    ] as const,
    queryFn: async (): Promise<ExampleCompaniesCheck | null> => {
      const { data } = await fetchClient.POST('/ai/example-companies', {
        body: {
          companies,
          employerSize,
          employerIndustries: industries,
          companyId: companyId || null,
        },
      });
      return data ?? null;
    },
    enabled: companies.trim().length > 0,
    retry: false,
    staleTime: Infinity,
  });
};
